import React, { useState, useEffect } from "react";
import {
    Users, ShieldCheck, ShieldAlert, Clock,
    Power, Calendar, Search, ExternalLink, Edit3,
    Save, X, CheckCircle, AlertCircle
} from "lucide-react";
import api from "../api/axiosInstance";
import toast, { Toaster } from "react-hot-toast";

const SuperAdminTenants = () => {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTenant, setSelectedTenant] = useState(null);

    useEffect(() => { fetchTenants(); }, []);

    const fetchTenants = async () => {
        const res = await api.get("get_tenants.php");
        setTenants(res.data.tenants || []);
        setLoading(false);
    };

    const handleUpdateStatus = async (id, currentStatus) => {
        try {
            await api.post("update_tenant_config.php", { id, is_active: (parseInt(currentStatus) === 1) ? 0 : 1 });
            toast.success("Tenant Status Updated");
            fetchTenants();
        } catch (e) { toast.error("Update Failed"); }
    };

    const handleUpdateExpiry = async (e) => {
        e.preventDefault();
        try {
            await api.post("update_tenant_config.php", {
                id: selectedTenant.id,
                subscription_end: selectedTenant.subscription_end
            });
            toast.success("Expiration Updated");
            setSelectedTenant(null);
            fetchTenants();
        } catch (e) { toast.error("Update Failed"); }
    };

    const getStatus = (tenant) => {
        const now = new Date();
        const expiry = new Date(tenant.subscription_end);
        if (!tenant.is_active) return { label: "Deactivated", color: "bg-rose-500", icon: <ShieldAlert size={14} /> };
        if (expiry < now) return { label: "Expired", color: "bg-amber-500", icon: <Clock size={14} /> };
        return { label: "Active", color: "bg-emerald-500", icon: <ShieldCheck size={14} /> };
    };

    if (loading) return <div className="p-20 text-center animate-pulse font-black text-slate-300">AUTHORIZING ADMIN ACCESS...</div>;

    return (
        <div className="p-8 space-y-8 bg-slate-50 min-h-screen">
            <Toaster position="top-right" />

            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-black italic tracking-tighter uppercase text-slate-900">Tenant Control</h1>
                    <p className="text-[10px] font-black uppercase text-indigo-500 tracking-widest mt-1 italic">IBOS Infrastructure Management</p>
                </div>
            </div>

            {/* Tenant Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tenants.map((tenant) => {
                    const status = getStatus(tenant);
                    return (
                        <div key={tenant.id} className="bg-white rounded-[40px] p-8 shadow-sm border border-slate-100 hover:shadow-2xl transition-all group">
                            <div className="flex justify-between items-start mb-6">
                                <div className={`px-4 py-1.5 rounded-full text-white text-[9px] font-black uppercase flex items-center gap-2 ${status.color}`}>
                                    {status.icon} {status.label}
                                </div>
                                <button
                                    onClick={() => handleUpdateStatus(tenant.id, tenant.is_active)}
                                    className={`p-3 rounded-2xl transition-all ${tenant.is_active ? 'bg-rose-50 text-rose-500 hover:bg-rose-500 hover:text-white' : 'bg-emerald-50 text-emerald-500 hover:bg-emerald-500 hover:text-white'}`}
                                >
                                    <Power size={18} />
                                </button>
                            </div>

                            <h2 className="text-2xl font-black italic uppercase tracking-tighter text-slate-900 leading-tight">
                                {tenant.company_name}
                            </h2>

                            <div className="mt-6 space-y-3">
                                <div className="flex items-center justify-between text-[10px] font-bold text-slate-400 uppercase">
                                    <span className="flex items-center gap-2"><Users size={12} /> Registered Users</span>
                                    <span className="text-slate-900">{tenant.user_count}</span>
                                </div>
                                <div className="flex items-center justify-between text-[10px] font-bold text-slate-400 uppercase">
                                    <span className="flex items-center gap-2"><Calendar size={12} /> Expires On</span>
                                    <span className="text-slate-900">
                                        {tenant.subscription_end ? new Date(tenant.subscription_end).toLocaleString() : 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <button
                                onClick={() => setSelectedTenant(tenant)}
                                className="w-full mt-8 py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase italic tracking-widest hover:bg-indigo-600 transition-all flex items-center justify-center gap-2"
                            >
                                <Edit3 size={14} /> Manage Access
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* EXPIRY UPDATE MODAL */}
            {selectedTenant && (
                <div className="fixed inset-0 z-[1000] bg-slate-900/90 backdrop-blur-xl flex items-center justify-center p-6">
                    <div className="bg-white w-full max-w-md rounded-[48px] p-12 space-y-8 animate-in zoom-in-95 duration-200 shadow-2xl">
                        <div className="flex justify-between items-center border-b pb-6">
                            <h2 className="text-2xl font-black italic uppercase tracking-tighter">Adjust Access</h2>
                            <button onClick={() => setSelectedTenant(null)} className="p-2 bg-slate-50 rounded-full text-rose-500"><X /></button>
                        </div>

                        <form onSubmit={handleUpdateExpiry} className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-slate-400 ml-2">New Expiry Timestamp</label>
                                <input
                                    type="datetime-local"
                                    className="w-full p-5 bg-slate-50 rounded-3xl font-black outline-none border-2 border-transparent focus:border-indigo-500 transition-all"
                                    defaultValue={selectedTenant.subscription_end ? selectedTenant.subscription_end.substring(0, 16) : ""}
                                    onChange={(e) => setSelectedTenant({ ...selectedTenant, subscription_end: e.target.value })}
                                />
                            </div>
                            <p className="text-[10px] text-slate-400 font-bold italic leading-relaxed">
                                Tip: For short demos, set time to 2 hours from now. Users will be logged out automatically after this time.
                            </p>
                            <button className="w-full py-5 bg-slate-900 text-white rounded-3xl font-black uppercase italic shadow-xl hover:bg-emerald-600 transition-all">
                                Update Access Window
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuperAdminTenants;